import React, { useMemo } from 'react';
import Select from 'react-select';

const norm = (s) => (s ?? '').toString().normalize('NFKC').trim().toLowerCase();

// Datum zusätzlich im deutschen Format durchsuchbar machen (2026-10-30 -> 30.10.2026)
const germanDate = (d) => {
    const m = /(\d{4})-(\d{2})-(\d{2})/.exec(d ?? '');
    return m ? `${m[3]}.${m[2]}.${m[1]}` : '';
};

const toOption = (v) => ({
    value: v.filename,
    video: v,
    search: norm([v.title, v.game, v.filename, v.release_date, germanDate(v.release_date)].join(' ')),
});

export default function VideoSelect({
    videos = [],
    value = '',
    onChange,
    placeholder = '-- Bitte Video auswählen --',
    className,
    disabled = false,
    style,
    darkMode = false,
}) {
    const options = useMemo(() => videos.map(toOption), [videos]);
    const selected = useMemo(() => options.find((o) => o.value === value) ?? null, [options, value]);

    // Jedes getippte Wort muss in Titel, Spiel, Dateiname oder Datum vorkommen.
    const filterOption = (option, input) => {
        const tokens = norm(input).split(/\s+/).filter(Boolean);
        return tokens.every((t) => option.data.search.includes(t));
    };

    // react-select ignoriert Bootstraps bg-*/text-*-Klassen; Darkmode daher explizit stylen.
    const styles = {
        container: (base) => ({ ...base, ...style }),
        control: (base, state) => ({
            ...base,
            borderRadius: '0.375rem',
            ...(darkMode
                ? { backgroundColor: '#2b3035', borderColor: state.isFocused ? '#6c757d' : '#495057' }
                : { borderColor: '#ced4da' }),
        }),
        singleValue: (base) => ({ ...base, color: darkMode ? '#f8f9fa' : base.color }),
        input: (base) => ({ ...base, color: darkMode ? '#f8f9fa' : base.color }),
        placeholder: (base) => ({ ...base, color: darkMode ? '#adb5bd' : base.color }),
        menu: (base) => ({ ...base, zIndex: 20, backgroundColor: darkMode ? '#2b3035' : base.backgroundColor }),
        option: (base, state) => ({
            ...base,
            ...(darkMode
                ? {
                    backgroundColor: state.isSelected ? '#495057' : state.isFocused ? '#3a4046' : '#2b3035',
                    color: '#f8f9fa',
                }
                : {}),
        }),
        noOptionsMessage: (base) => ({ ...base, color: darkMode ? '#adb5bd' : base.color }),
        dropdownIndicator: (base) => ({ ...base, color: darkMode ? '#adb5bd' : base.color }),
        clearIndicator: (base) => ({ ...base, color: darkMode ? '#adb5bd' : base.color }),
        indicatorSeparator: (base) => ({ ...base, backgroundColor: darkMode ? '#495057' : base.backgroundColor }),
    };

    const muted = darkMode ? '#adb5bd' : '#6c757d';

    return (
        <Select
            options={options}
            value={selected}
            onChange={(opt) => onChange?.(opt ? opt.value : '')}
            filterOption={filterOption}
            isDisabled={disabled}
            isClearable
            isSearchable
            openMenuOnFocus
            menuPlacement="auto"
            maxMenuHeight={320}
            placeholder={placeholder}
            className={className}
            styles={styles}
            noOptionsMessage={({ inputValue }) => `Keine Videos für „${inputValue}“ gefunden`}
            formatOptionLabel={({ video }, { context }) => {
                const meta = [video.release_date, video.game].filter(Boolean).join(' · ');
                if (context === 'value') {
                    return <span>{video.title || video.filename}</span>;
                }
                return (
                    <div>
                        <div>{video.title || video.filename}</div>
                        <div style={{ fontSize: '0.8em', color: muted }}>
                            {video.filename}{meta ? ` · ${meta}` : ''}
                        </div>
                    </div>
                );
            }}
        />
    );
}